"use client";

import { useCallback } from "react";

type LessonApprovalFilter = "all" | "approved" | "unapproved";
type LessonQuizFilter = "all" | "with_quiz" | "no_quiz";
type LessonCanonicalFilter = "all" | "with_canonical" | "no_canonical";

export function useLessonFilters({
  pathname,
  routerReplace,
  searchParams,
}: {
  pathname: string | null;
  routerReplace: (url: string) => void;
  searchParams: Pick<URLSearchParams, "get" | "toString">;
}) {
  const lessonApprovalFilter: LessonApprovalFilter = (() => {
    const approval = searchParams.get("approval");
    return approval === "approved" || approval === "unapproved" ? approval : "all";
  })();
  const lessonQuizFilter: LessonQuizFilter = (() => {
    const quiz = searchParams.get("quiz");
    return quiz === "with_quiz" || quiz === "no_quiz" ? quiz : "all";
  })();
  const lessonCanonicalFilter: LessonCanonicalFilter = (() => {
    const canonical = searchParams.get("canonical");
    return canonical === "with_canonical" || canonical === "no_canonical" ? canonical : "all";
  })();
  const lessonTypeFilter = (() => {
    const lessonType = searchParams.get("lesson_type");
    return lessonType && lessonType.trim().length > 0 ? lessonType : "all";
  })();

  const hasActiveLessonFilters =
    lessonApprovalFilter !== "all" ||
    lessonQuizFilter !== "all" ||
    lessonCanonicalFilter !== "all" ||
    lessonTypeFilter !== "all";

  const updateFilter = useCallback((key: string, value: string) => {
    if (!pathname) return;
    const params = new URLSearchParams(searchParams.toString());
    if (value === "all") params.delete(key);
    else params.set(key, value);
    const next = params.toString();
    routerReplace(next ? `${pathname}?${next}` : pathname);
  }, [pathname, routerReplace, searchParams]);

  const resetLessonFilters = () => {
    if (!pathname) return;
    routerReplace(pathname);
  };

  return {
    lessonApprovalFilter,
    setLessonApprovalFilter: (value: LessonApprovalFilter) => updateFilter("approval", value),
    lessonQuizFilter,
    setLessonQuizFilter: (value: LessonQuizFilter) => updateFilter("quiz", value),
    lessonCanonicalFilter,
    setLessonCanonicalFilter: (value: LessonCanonicalFilter) => updateFilter("canonical", value),
    lessonTypeFilter,
    setLessonTypeFilter: (value: string) => updateFilter("lesson_type", value),
    hasActiveLessonFilters,
    resetLessonFilters,
  };
}
